"use client";

import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import { errorMessage } from "@/src/core/api/validation";
import {
  bookingApi,
  bookingKeys,
  bookingService,
  type IBookingItem,
} from "@/src/shared/features/booking";
import {
  paymentApi,
  paymentKeys,
  paymentService,
  usePaymentGateway,
  useWalletBalanceQuery,
} from "@/src/shared/features/payment";
import type { IFilter } from "@/src/shared/features/search";
import type { IListing } from "@/src/shared/types/listing";
import { formatYearDate } from "@/src/shared/utils/timeFunctionsModel";
import useAnalytics from "@/src/lib/useAnalytics";
import { useStayPriceQuery } from "@/src/features/listing/api/queries";
import { listingService } from "@/src/features/listing/services";
import {
  confirmPayService,
  type PaymentTiming,
} from "@/src/features/listing/confirmPay";

interface IUseConfirmAndPayArgs {
  listing: IListing;
  filters: IFilter;
  isOpen: boolean;
  onNeedDates: () => void;
}

const WEB_PLATFORM = 3;

/**
 * The app's `ConfirmAndPayController`: prices the stay, splits it between the wallet and a
 * gateway, creates the booking on confirm (once per stay) and sends the guest to pay.
 */
export function useConfirmAndPay({
  listing,
  filters,
  isOpen,
  onNeedDates,
}: IUseConfirmAndPayArgs) {
  const router = useAppRouter();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const analytics = useAnalytics();

  const [timing, setTiming] = useState<PaymentTiming>("full");
  const gatewayChoice = usePaymentGateway(isOpen);
  const [isUsingWallet, setIsUsingWallet] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [booked, setBooked] = useState<{
    key: string;
    booking: IBookingItem;
    minimum: number;
  } | null>(null);
  const hasPaid = useRef(false);

  const { checkIn, checkOut, guests } = filters;
  const nights = confirmPayService.nights(checkIn, checkOut);
  const hasStayDates = nights > 0;
  const guestCount = guests.adults + guests.children;
  const from = checkIn ? formatYearDate(checkIn) : "";
  const to = checkOut ? formatYearDate(checkOut) : "";

  const priceParams = useMemo(
    () =>
      isOpen && hasStayDates && guestCount > 0
        ? { guest: guestCount, from, to }
        : null,
    [isOpen, hasStayDates, guestCount, from, to],
  );
  const priceQuery = useStayPriceQuery(listing.id, priceParams);
  const totals = confirmPayService.totals(priceQuery.data, nights);

  const { data: wallet, refetch: refetchBalance } =
    useWalletBalanceQuery(isOpen);
  const balance = paymentService.spendable(wallet?.balance);
  const minGatewayAmount = paymentService.minGatewayAmount(
    wallet?.minGatewayAmount,
  );

  const payload = {
    message: "",
    from,
    to,
    listing_id: listing.id,
    guests: guests.adults,
    child: guests.children,
    infant: guests.infants,
    os_platform: WEB_PLATFORM,
  };
  const payloadKey = JSON.stringify(payload);
  const bookedNow = booked?.key === payloadKey ? booked : null;

  const payableTotal = bookedNow
    ? bookedNow.booking.total_payable - bookedNow.booking.paid
    : (totals?.total ?? 0);
  const minimum = confirmPayService.minimumPayable(
    priceQuery.data,
    bookedNow?.minimum ?? null,
    !!bookedNow,
  );
  const canPayPartially = confirmPayService.canPayPartially(
    minimum,
    payableTotal,
  );
  const amountDue = confirmPayService.amountDueNow(
    timing,
    minimum,
    payableTotal,
  );
  const split = confirmPayService.walletSplit(
    balance,
    amountDue,
    isUsingWallet,
    minGatewayAmount,
  );
  const isPriceKnown = !priceQuery.isFetching && totals !== null;
  const guestsFit = listingService.guestsFit(guests, {
    adults: listing.max_guest,
    children: listing.max_child,
    infants: listing.max_infant,
  });

  const showError = (message: string) =>
    enqueueSnackbar(message, { variant: "error" });

  const toggleWallet = (isOn: boolean) => {
    if (isPaying) return;
    setIsUsingWallet(isOn);
    void refetchBalance();
  };

  const prepareBooking = async () => {
    if (bookedNow) return bookedNow;
    const booking = await bookingApi.create(payload);
    const minimumPayment = await paymentApi.getMinimumPayment(booking.id);
    const next = { key: payloadKey, booking, minimum: minimumPayment };
    setBooked(next);
    return next;
  };

  /** Creates the booking if needed and pays; resolves true once the guest leaves to pay. */
  const pay = async (): Promise<boolean> => {
    if (isPaying || hasPaid.current) return false;
    if (!hasStayDates) {
      onNeedDates();
      return false;
    }
    if (!guestsFit) {
      showError("Please change your guests to fit this place");
      return false;
    }
    setIsPaying(true);
    const useWallet = split.walletAmount > 0;
    try {
      const { booking, minimum: bookedMinimum } = await prepareBooking();
      const total = booking.total_payable - booking.paid;
      if (
        timing === "part" &&
        !confirmPayService.canPayPartially(bookedMinimum, total)
      ) {
        setTiming("full");
        showError("Partial payment is not available for this booking");
        return false;
      }
      const amount = timing === "full" ? total : bookedMinimum;
      const amountError = confirmPayService.paymentAmountError(
        amount,
        booking,
        bookedMinimum,
        minGatewayAmount,
      );
      if (amountError) {
        showError(amountError);
        return false;
      }
      analytics.begin_checkout(bookingService.checkoutEvent(booking, amount));
      const { provider, action } = gatewayChoice.request(
        split.isPaidByWalletOnly,
      );
      const result = await paymentApi.startPayment(booking.id, {
        amount,
        provider,
        action,
        ...(useWallet && { use_wallet: 1 as const }),
      });
      const nextUrl = paymentService.nextUrl(booking.id, result);
      if (!nextUrl) {
        showError("Payment URL not found");
        return false;
      }
      hasPaid.current = true;
      if (result.paid) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: bookingKeys.all }),
          queryClient.invalidateQueries({ queryKey: paymentKeys.all }),
        ]);
      }
      void router.push(nextUrl);
      return true;
    } catch (error) {
      if (useWallet) void refetchBalance();
      showError(errorMessage(error, "Something went wrong, try again!"));
      return false;
    } finally {
      setIsPaying(false);
    }
  };

  return {
    timing,
    selectTiming: setTiming,
    gatewayChoice,
    isPaying,
    pay,
    hasStayDates,
    guestsFit,
    isPriceLoading: priceQuery.isFetching,
    totals,
    nights,
    payableTotal,
    minimum,
    canPayPartially,
    lastPartialPayment: priceQuery.data?.last_time_partial_payment ?? null,
    wallet: {
      balance,
      isAvailable: balance >= 1,
      isOn: isUsingWallet && balance >= 1,
      toggle: toggleWallet,
      ...split,
      heldBackNotice: confirmPayService.heldBackNotice(
        gatewayChoice.gateway,
        minGatewayAmount,
        split.walletAmount,
        split.heldBack,
      ),
    },
    confirmLabel: confirmPayService.confirmLabel(isPriceKnown, split.remaining),
    needsMethodChoice:
      !gatewayChoice.hasSavedBkash &&
      !split.isPaidByWalletOnly &&
      hasStayDates &&
      guestsFit,
  };
}
